export interface ActualOptions {
  absolute?: boolean;
  clone?: boolean;
  includeMargin?: boolean;
  display?: string;
}

export type MeasurementMethod =
  | 'width'
  | 'height'
  | 'innerWidth'
  | 'innerHeight'
  | 'outerWidth'
  | 'outerHeight'
  | 'scrollWidth'
  | 'scrollHeight'
  | 'offsetWidth'
  | 'offsetHeight'
  | 'clientWidth'
  | 'clientHeight';

/**
 * Gets the actual measurement of a hidden element by temporarily making it visible.
 *
 * @param element - The HTMLElement to measure.
 * @param method - The measurement method (e.g., 'width', 'height', 'scrollHeight', etc.).
 * @param options - Optional configuration to control the measurement behavior.
 * @returns The measured value as a number.
 * @throws Will throw an error if the specified method is not supported.
 */
function getActual(
  element: HTMLElement,
  method: MeasurementMethod,
  options?: ActualOptions
): number {
  const defaultOptions: Required<ActualOptions> = {
    absolute: false,
    clone: false,
    includeMargin: false,
    display: 'block',
  };

  const config: Required<ActualOptions> = { ...defaultOptions, ...options };

  let targetElement: HTMLElement = element;
  let clonedElement: HTMLElement | null = null;

  const hiddenAncestors: HTMLElement[] = [];
  const originalStyles: string[] = [];

  try {
    if (config.clone) {
      clonedElement = cloneElementWithStyles(element);
      document.body.appendChild(clonedElement);
      targetElement = clonedElement;
    } else {
      hiddenAncestors.push(...findHiddenAncestors(element));
      originalStyles.push(...applyTemporaryStyles(hiddenAncestors, config));
    }

    if (isIframe(targetElement)) {
      const iframeBody = getIframeBody(targetElement as HTMLIFrameElement);
      if (iframeBody) {
        targetElement = iframeBody;
      } else {
        throw new Error('Unable to access iframe content.');
      }
    }

    return measureElement(targetElement, method, config);
  } finally {
    if (clonedElement) {
      clonedElement.remove();
    } else {
      restoreOriginalStyles(hiddenAncestors, originalStyles);
    }
  }
}

/**
 * Clones an element along with its computed styles.
 *
 * @param element - The HTMLElement to clone.
 * @returns A deep clone of the element with copied styles.
 */
function cloneElementWithStyles(element: HTMLElement): HTMLElement {
  const clone = element.cloneNode(true) as HTMLElement;
  const computedStyles = getComputedStyle(element);

  Array.from(computedStyles).forEach((prop) => {
    clone.style.setProperty(prop, computedStyles.getPropertyValue(prop));
  });

  // Apply styles to position the clone off-screen and make it invisible
  Object.assign(clone.style, {
    position: 'absolute',
    top: '-10000px',
    left: '-10000px',
    visibility: 'hidden',
    pointerEvents: 'none',
  });

  return clone;
}

/**
 * Finds all ancestor elements that are hidden (display: none).
 *
 * @param element - The HTMLElement whose ancestors are to be checked.
 * @returns An array of hidden ancestor HTMLElements.
 */
function findHiddenAncestors(element: HTMLElement): HTMLElement[] {
  const hidden: HTMLElement[] = [];
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const style = getComputedStyle(current);
    if (style.display === 'none') {
      hidden.push(current);
    }
    current = current.parentElement;
  }

  return hidden;
}

/**
 * Applies temporary styles to make hidden elements visible for measurement.
 *
 * @param elements - The hidden HTMLElements to style.
 * @param config - Configuration options.
 * @returns An array of original style strings for restoration.
 */
function applyTemporaryStyles(
  elements: HTMLElement[],
  config: Required<ActualOptions>
): string[] {
  return elements.map((el) => {
    const originalStyle = el.getAttribute('style') || '';
    let tempStyles = `visibility: hidden !important; display: ${config.display} !important;`;
    if (config.absolute) {
      tempStyles += ' position: absolute !important;';
    }
    el.style.cssText += tempStyles;
    return originalStyle;
  });
}

/**
 * Restores the original styles of elements.
 *
 * @param elements - The HTMLElements whose styles are to be restored.
 * @param styles - The original style strings.
 */
function restoreOriginalStyles(
  elements: HTMLElement[],
  styles: string[]
): void {
  elements.forEach((el, index) => {
    const originalStyle = styles[index];
    if (originalStyle) {
      el.setAttribute('style', originalStyle);
    } else {
      el.removeAttribute('style');
    }
  });
}

/**
 * Checks if an element is an iframe.
 *
 * @param element - The HTMLElement to check.
 * @returns True if the element is an iframe, else false.
 */
function isIframe(element: HTMLElement): boolean {
  return element.nodeName.toLowerCase() === 'iframe';
}

/**
 * Retrieves the body of an iframe's content document.
 *
 * @param iframe - The HTMLIFrameElement.
 * @returns The body HTMLElement of the iframe, or null if inaccessible.
 */
function getIframeBody(iframe: HTMLIFrameElement): HTMLElement | null {
  try {
    return iframe.contentDocument?.body || null;
  } catch {
    return null;
  }
}

/**
 * Measures the specified property of an element.
 *
 * @param element - The HTMLElement to measure.
 * @param method - The measurement method.
 * @param config - Configuration options.
 * @returns The measured value as a number.
 * @throws Will throw an error if the method is unsupported.
 */
function measureElement(
  element: HTMLElement,
  method: MeasurementMethod,
  config: Required<ActualOptions>
): number {
  const measurementFunctions: Record<MeasurementMethod, () => number> = {
    width: () => element.getBoundingClientRect().width,
    height: () => element.getBoundingClientRect().height,
    innerWidth: () => element.clientWidth,
    innerHeight: () => element.clientHeight,
    outerWidth: () => {
      let value = element.offsetWidth;
      if (config.includeMargin) {
        value += getHorizontalMargins(element);
      }
      return value;
    },
    outerHeight: () => {
      let value = element.offsetHeight;
      if (config.includeMargin) {
        value += getVerticalMargins(element);
      }
      return value;
    },
    scrollWidth: () => element.scrollWidth,
    scrollHeight: () => element.scrollHeight,
    offsetWidth: () => element.offsetWidth,
    offsetHeight: () => element.offsetHeight,
    clientWidth: () => element.clientWidth,
    clientHeight: () => element.clientHeight,
  };

  const measure = measurementFunctions[method];
  if (!measure) {
    throw new Error(`getActual => The method "${method}" is not supported.`);
  }

  return measure();
}

/**
 * Calculates the total horizontal margins of an element.
 *
 * @param element - The HTMLElement.
 * @returns The sum of left and right margins.
 */
function getHorizontalMargins(element: HTMLElement): number {
  const style = getComputedStyle(element);
  const marginLeft = parseFloat(style.marginLeft) || 0;
  const marginRight = parseFloat(style.marginRight) || 0;
  return marginLeft + marginRight;
}

/**
 * Calculates the total vertical margins of an element.
 *
 * @param element - The HTMLElement.
 * @returns The sum of top and bottom margins.
 */
function getVerticalMargins(element: HTMLElement): number {
  const style = getComputedStyle(element);
  const marginTop = parseFloat(style.marginTop) || 0;
  const marginBottom = parseFloat(style.marginBottom) || 0;
  return marginTop + marginBottom;
}

export default getActual;
